import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Outlet } from 'react-router-dom';
import TournamentTabBar from '../../components/Portal/TournamentTabBar';
import WaitlistBanner from '../../components/Portal/WaitlistBanner';
import { portalTournamentAPI } from '../../services/api';
import { getPhaseLabel } from '../../utils/tournamentPhases';

/**
 * PortalTournamentDetail - Wrapper for a single tournament in the player portal.
 * Renders a header, tab bar, and outlet for the active tab.
 */
const PortalTournamentDetail = () => {
  const { tournamentId } = useParams();
  const navigate = useNavigate();
  const [tournament, setTournament] = useState(null);
  const [player, setPlayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [previousPosition, setPreviousPosition] = useState(null);

  const fetchDetail = async () => {
    try {
      if (!loading) {
        // Only show loading spinner on initial load
        setLoading(false);
      }
      const res = await portalTournamentAPI.getTournamentDetail(tournamentId);
      const data = res.data?.data || {};
      const newPlayer = data.player || null;

      // Track position changes
      if (newPlayer?.waitlistData?.position) {
        if (previousPosition !== null && previousPosition !== newPlayer.waitlistData.position) {
          // Position changed - could add notification here
          console.log(`Waitlist position changed: ${previousPosition} → ${newPlayer.waitlistData.position}`);
        }
        setPreviousPosition(newPlayer.waitlistData.position);
      }

      setTournament(data.tournament || null);
      setPlayer(newPlayer);
    } catch (err) {
      console.error('Failed to load tournament detail:', err);
      setError('Failed to load tournament.');
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    setLoading(true);
    fetchDetail();
  }, [tournamentId]);

  // Polling for waitlisted players to show position updates
  useEffect(() => {
    if (!player || player.rosterStatus !== 'waitlisted') {
      return; // Only poll if player is waitlisted
    }

    const pollInterval = setInterval(() => {
      fetchDetail();
    }, 30000); // Poll every 30 seconds

    return () => clearInterval(pollInterval);
  }, [tournamentId, player?.rosterStatus]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin h-8 w-8 border-4 border-green-500 border-t-transparent rounded-full" />
        </div>
      </div>
    );
  }

  if (error || !tournament) {
    return (
      <div className="min-h-screen bg-gray-50 px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 mb-4">
          {error || 'Tournament not found.'}
        </div>
        <button
          onClick={() => navigate('/portal/tournaments')}
          className="text-green-600 text-sm font-medium"
        >
          Back to Tournaments
        </button>
      </div>
    );
  }

  const portalStatus = tournament.portalStatus || 'signup';
  const tournamentDate = tournament.date ? new Date(tournament.date) : null;

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Tournament Header */}
      <header className="bg-green-700 text-white px-4 py-3 sticky top-0 z-40">
        <div className="max-w-lg mx-auto">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/portal/tournaments')}
              className="p-1 hover:bg-green-600 rounded-lg transition-colors"
              aria-label="Back"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div className="flex-1 min-w-0">
              <h1 className="font-semibold text-base truncate">
                {tournament.name}
              </h1>
              <div className="flex items-center gap-2 text-green-100 text-xs">
                {tournamentDate && (
                  <span>{tournamentDate.toLocaleDateString()}</span>
                )}
                <span className="bg-white/20 px-2 py-0.5 rounded-full text-white">
                  {getPhaseLabel(portalStatus)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Waitlist Banner - Show if player is waitlisted */}
      {player?.rosterStatus === 'waitlisted' && player?.waitlistData && (
        <div className="max-w-lg mx-auto px-4 pt-4">
          <WaitlistBanner waitlistData={player.waitlistData} />
        </div>
      )}

      {/* Tab Bar */}
      <TournamentTabBar portalStatus={portalStatus} />

      {/* Tab Content */}
      <main className="max-w-lg mx-auto px-4 py-4">
        <Outlet context={{ tournament, player, portalStatus }} />
      </main>
    </div>
  );
};

export default PortalTournamentDetail;
